import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Sparkles,
  Lightbulb,
  Gift,
  Plus,
  PlayCircle,
  LucideIcon,
} from "lucide-react";
import { UserContext } from "components/context/UserContext";
import MyRepCares from "components/MyRepCares";
import ReferralsButton from "components/rep/referral/ReferralsButton";
import SupademoGlobal from "components/supademos/SupademoGlobal";
import "./FabMenu.css";

declare const Supademo: JObj;

const COMPACT_BREAKPOINT = 768;

interface FabMenuProps {
  showFab: boolean;
  showSupademos: boolean;
  isRep: boolean;
}

const FabMenu: React.FC<FabMenuProps> = ({ showFab, showSupademos, isRep }) => {
  const { state } = useContext(UserContext)!;

  const [isCompact, setIsCompact] = useState<boolean>(
    typeof window !== "undefined"
      ? window.innerWidth < COMPACT_BREAKPOINT
      : false,
  );
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [caresOpen, setCaresOpen] = useState<boolean>(false);
  const [referralsOpen, setReferralsOpen] = useState<boolean>(false);

  useEffect(() => {
    const handleResize = () => {
      setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-close the compact menu when user switches to wide screen
  useEffect(() => {
    if (!isCompact && menuOpen) setMenuOpen(false);
  }, [isCompact, menuOpen]);

  const supaDemoKey = useMemo(
    () =>
      state?.currentUser?.roleId === 8 || state?.currentUser?.roleId === 2
        ? process.env.REACT_APP_OFFICE_SUPADEMO_KEY
        : process.env.REACT_APP_REPS_SUPADEMO_KEY,
    [state?.currentUser],
  );

  const handleOpenSupademo = useCallback(() => {
    if (!supaDemoKey) return;
    try {
      Supademo?.widget?.open(`${supaDemoKey}`);
    } catch (e) {
      console.warn("Failed to open Supademo:", e);
    }
  }, [supaDemoKey]);

  const handleOpenCares = useCallback(() => {
    setMenuOpen(false);
    setCaresOpen(true);
  }, []);

  const handleOpenReferrals = useCallback(() => {
    setMenuOpen(false);
    setReferralsOpen(true);
  }, []);

  const handleOpenDemo = useCallback(() => {
    setMenuOpen(false);
    handleOpenSupademo();
  }, [handleOpenSupademo]);

  // Build the list of compact menu items based on feature flags
  const compactItems = useMemo(() => {
    const items: Array<{
      key: string;
      label: string;
      Icon: LucideIcon;
      onClick: () => void;
      tone: "blue" | "white" | "accent";
    }> = [];

    if (showSupademos && supaDemoKey) {
      items.push({
        key: "supademo",
        label: "See How To Use MyRep",
        Icon: PlayCircle,
        onClick: handleOpenDemo,
        tone: "accent",
      });
    }
    if (showFab) {
      items.push({
        key: "cares",
        label: "MyRep Cares",
        Icon: Lightbulb,
        onClick: handleOpenCares,
        tone: "blue",
      });
    }
    if (isRep && showFab) {
      items.push({
        key: "referrals",
        label: "Referrals",
        Icon: Gift,
        onClick: handleOpenReferrals,
        tone: "white",
      });
    }
    return items;
  }, [
    showSupademos,
    supaDemoKey,
    showFab,
    isRep,
    handleOpenDemo,
    handleOpenCares,
    handleOpenReferrals,
  ]);

  // Close the expanded menu when clicking outside (on the backdrop)
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <>
      {/*
        The three child components are ALWAYS mounted so that their internal
        drawers / modals are not destroyed when the compact menu closes. Their
        visible trigger buttons are hidden via `hideTrigger` on small screens
        and shown normally on wide screens.
      */}
      <div className="fab-container" aria-hidden={isCompact}>
        {showSupademos && <SupademoGlobal hideTrigger={isCompact} />}
        {showFab && (
          <MyRepCares
            hideTrigger={isCompact}
            isOpen={caresOpen}
            onOpenChange={setCaresOpen}
          />
        )}
        {isRep && showFab && (
          <ReferralsButton
            hideTrigger={isCompact}
            isOpen={referralsOpen}
            onOpenChange={setReferralsOpen}
          />
        )}
      </div>

      {/* Compact single-FAB with expandable vertical menu (small screens) */}
      {isCompact && compactItems.length > 0 && (
        <>
          <AnimatePresence>
            {menuOpen && (
              <motion.button
                key="fab-backdrop"
                className="fab-menu-backdrop"
                aria-label="Close quick actions"
                onClick={closeMenu}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              />
            )}
          </AnimatePresence>

          <div className="fab-compact">
            <AnimatePresence>
              {menuOpen && (
                <motion.ul
                  key="fab-menu"
                  className="fab-menu-list"
                  initial={{ opacity: 0, y: 12, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 12, scale: 0.96 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  {compactItems.map((item, idx) => (
                    <motion.li
                      key={item.key}
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 16 }}
                      transition={{
                        duration: 0.18,
                        delay: menuOpen ? idx * 0.04 : 0,
                      }}
                    >
                      <button
                        type="button"
                        className={`fab-menu-item fab-menu-item--${item.tone}`}
                        onClick={item.onClick}
                      >
                        <span
                          className="fab-menu-item__icon"
                          aria-hidden="true"
                        >
                          <item.Icon size={18} />
                        </span>
                        <span className="fab-menu-item__label">
                          {item.label}
                        </span>
                      </button>
                    </motion.li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>

            <motion.button
              type="button"
              className={`fab-main-btn ${menuOpen ? "fab-main-btn--open" : ""}`}
              onClick={() => setMenuOpen((p) => !p)}
              aria-label={
                menuOpen ? "Close quick actions" : "Open quick actions"
              }
              aria-expanded={menuOpen}
              whileTap={{ scale: 0.92 }}
            >
              <motion.span
                className="fab-main-btn__icon"
                animate={{ rotate: menuOpen ? 45 : 0 }}
                transition={{ duration: 0.2 }}
              >
                {menuOpen ? <Plus size={22} /> : <Sparkles size={20} />}
              </motion.span>
            </motion.button>
          </div>
        </>
      )}
    </>
  );
};

export default FabMenu;
